import { useRef, useState } from 'react';
import { ChevronIcon, ClockIcon, ColumnsIcon, GridIcon } from 'assets/icons';
import { VaultNavigationTabs } from 'common/enums';
import { DropDownListHeader } from 'common/drop-down-list-header';
import { DropDownListItem } from 'common/drop-down-list-item';
import { Menu, PositionType } from 'common/menu';
import { classnames } from 'helpers/utils';
import { useClickOutside } from 'hooks/click-outside';
import { navigationTabs } from 'pages/vault-detail/components/tabs/navigation-tabs';
import { goToPage, RouteName } from 'routes';
import {
  AlbumSortKey,
  AlbumViewKind,
  albumSortOptions,
} from '../../single-album-utils';
import styles from './album-nav-row.module.scss';

interface AlbumNavRowProps {
  vaultId: number;
  view: AlbumViewKind;
  onChangeView: (view: AlbumViewKind) => void;
  sortBy: AlbumSortKey;
  onChangeSort: (sortBy: AlbumSortKey) => void;
  // The album has been arranged by hand, so "Custom order" is offered.
  showCustomOrder?: boolean;
  // Phones: only the Grid / Planner tabs, across the screen as in the mobile
  // app; back and sort are in the phone header (AlbumPhoneHeader).
  phone?: boolean;
}

const viewTabs: {
  key: AlbumViewKind;
  label: string;
  icon: React.ReactNode;
}[] = [
  { key: 'grid', label: 'Grid', icon: <GridIcon /> },
  { key: 'planner', label: 'Planner', icon: <ColumnsIcon /> },
];

export const AlbumNavRow = ({
  vaultId,
  view,
  onChangeView,
  sortBy,
  onChangeSort,
  showCustomOrder = false,
  phone = false,
}: AlbumNavRowProps) => {
  const sortOptions = albumSortOptions(showCustomOrder);
  const [showSort, setShowSort] = useState(false);
  const sortRef = useRef<HTMLDivElement>(null);
  useClickOutside(sortRef, () => setShowSort(false));

  const goToVaultTab = (tab: VaultNavigationTabs) =>
    goToPage(RouteName.VaultDetail, { id: vaultId }, { tab });

  const sortLabel =
    sortOptions.find((option) => option.value === sortBy)?.label ?? 'Sort';

  const tabs = (
    <div className={styles.viewTabs} role="tablist">
      {viewTabs.map((tab) => (
        <button
          key={tab.key}
          type="button"
          role="tab"
          aria-selected={view === tab.key}
          className={classnames(styles.viewTab, {
            [styles.viewTabActive]: view === tab.key,
          })}
          onClick={() => onChangeView(tab.key)}
        >
          {tab.icon}
          <span>{tab.label}</span>
          <span className={styles.viewTabUnderline} />
        </button>
      ))}
    </div>
  );

  if (phone) return <div className={styles.navRowPhone}>{tabs}</div>;

  return (
    <div className={styles.navRow}>
      <div className={styles.leftZone}>
        <button
          type="button"
          className={styles.backButton}
          aria-label="Back to albums"
          onClick={() => goToVaultTab(VaultNavigationTabs.albums)}
        >
          <ChevronIcon className={styles.backIcon} />
        </button>
        {tabs}
      </div>

      {/* The Albums pill stays active on the single-album screen no matter
          where the album was opened from (dashboard included). */}
      <div className={styles.pill}>
        {navigationTabs.map((tab) => (
          <button
            key={tab.name}
            type="button"
            className={classnames(styles.pillTab, {
              [styles.pillTabActive]: tab.tab === VaultNavigationTabs.albums,
            })}
            onClick={() => goToVaultTab(tab.tab)}
          >
            {tab.name}
          </button>
        ))}
      </div>

      <div className={styles.rightZone}>
        <div className={styles.sortWrap} ref={sortRef}>
          <button
            type="button"
            className={styles.sortButton}
            onClick={() => setShowSort((prev) => !prev)}
          >
            <ClockIcon className={styles.sortIcon} />
            <span className={styles.sortLabel}>{sortLabel}</span>
            <ChevronIcon className={styles.sortChevron} />
          </button>
          {showSort && (
            <Menu
              position={PositionType.BottomRight}
              className={styles.sortMenu}
            >
              <DropDownListHeader title="Sort by:" />
              {sortOptions.map((option) => (
                <DropDownListItem
                  key={option.value}
                  leftIcon={<ClockIcon />}
                  onClick={() => {
                    onChangeSort(option.value);
                    setShowSort(false);
                  }}
                >
                  {option.label}
                </DropDownListItem>
              ))}
            </Menu>
          )}
        </div>
      </div>
    </div>
  );
};
